import { Fragment, useState } from "react";

import cx from "@hsl/core/utils/cx";
import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { Translate } from "../../../hooks/useTranslation";
import useExplorerStore from "../../../store/store";
import { ControlledCollapsible } from "../../Collapsible";
import { type Props as FundCardTableProps } from "../../FundCardTables/FundCardTable";
import Table from "../../FundCardTables/Table";
import { Footnote } from "../../Table";
import { NoData } from "./Messages";

interface Props extends FundCardTableProps {}

const FundCardPricesPMC = ({ config, shareClasses, subTabs }: Props) => {
    const [openTabs, setOpenTabs] = useState([0]);
    const indexedData = useExplorerStore((state) => state.indexedData);
    const priceRegEx = /^price.*(?<!(currency))$/gi;
    const prefacesComponents = subTabs
        ? subTabs
              .map((stab, i, arr) => {
                  const priceColumns = stab.columns?.filter((col) =>
                      priceRegEx.test(col.prop),
                  );
                  const scData = shareClasses
                      .filter(
                          (sc) =>
                              priceColumns &&
                              priceColumns.some(
                                  (col) =>
                                      indexedData![sc]?.data[col.prop] !== "-",
                              ) &&
                              (stab.restrictions ?? []).every(
                                  (r) =>
                                      r.type !== "shareclass" ||
                                      r.value.includes(
                                          indexedData![sc]?.data[
                                              r.prop
                                          ] as string,
                                      ),
                              ),
                      )
                      .map((sc) => indexedData![sc]);
                  const isOpen = openTabs.includes(i);
                  return scData.length
                      ? [
                            stab.preface,
                            <ControlledCollapsible
                                key={stab.key}
                                label={
                                    <span className="whitespace-pre">
                                        <Translate>{stab.label}</Translate>
                                    </span>
                                }
                                labelClassNames={cx(
                                    "border-platinum font-medium w-full flex items-center z-30 relative",
                                    i === 0 && "mt-2 sm:mt-4",
                                    isOpen && "sm:border-b pb-2",
                                )}
                                className={
                                    arr.length - 1 === i
                                        ? "mb-1 sm:mb-2"
                                        : "mb-2 sm:mb-3"
                                }
                                withChevron
                                isOpen={isOpen}
                                onClick={() => toggleOpen(i)}
                            >
                                <Table
                                    tab={stab}
                                    config={config}
                                    data={
                                        scData as unknown as TopLevelDataObj[]
                                    }
                                    hidePreface
                                />
                            </ControlledCollapsible>,
                        ]
                      : false;
              })
              .filter((tuple): tuple is [string | undefined, JSX.Element] =>
                  Boolean(tuple),
              )
        : [];
    return prefacesComponents.length ? (
        <>
            {prefacesComponents.map(([_, component], i) => (
                <Fragment key={`prices-collapsible-${i}`}>{component}</Fragment>
            ))}
            <Footnote>
                {prefacesComponents
                    .map(([preface, _]) => preface)
                    .join("<br/>")}
            </Footnote>
        </>
    ) : (
        <NoData />
    );

    function toggleOpen(i: number) {
        setOpenTabs((open) => {
            if (open.includes(i)) {
                return open.filter((o) => o !== i);
            } else {
                return [...open, i];
            }
        });
    }
};

export default FundCardPricesPMC;
